import { useContext, useEffect, useRef } from 'react';

import { MobileContext } from './MobileContextProvider';
import type { MobileContextType, UseMobileContextOptions } from './types';

/**
 * Enhanced Mobile Context Hook
 *
 * This hook provides access to the comprehensive mobile context with:
 * - Type-safe context consumption
 * - Error boundaries for missing provider
 * - Performance optimizations
 * - Development warnings and debugging
 * - Backwards compatibility with existing useMobileDetection
 */

/**
 * Main hook for consuming mobile context
 */
export function useMobileContext(options: UseMobileContextOptions = {}): MobileContextType {
  const context = useContext(MobileContext);
  const { debug = false, subscriptions } = options;

  // Development error checking
  if (context === undefined) {
    throw new Error('useMobileContext must be used within a MobileContextProvider. ' + 'Make sure to wrap your component tree with <MobileContextProvider>.');
  }

  // Performance monitoring in development
  const renderCountRef = useRef(0);
  const lastContextRef = useRef(context);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && debug) {
      renderCountRef.current++;

      // Check for unnecessary re-renders
      const contextChanged = lastContextRef.current !== context;
      if (contextChanged) {
        console.debug('[useMobileContext] Context changed:', {
          renderCount: renderCountRef.current,
          deviceType: context.deviceType,
          viewport: `${context.viewportWidth}x${context.viewportHeight}`,
          isMobile: context.isMobile,
        });
      }

      lastContextRef.current = context;
    }
  }, [context, debug]);

  // If subscriptions are specified, could implement selective updates here
  // For now, return full context (future optimization opportunity)
  return context;
}

/**
 * Backwards compatibility hook - maintains existing useMobileDetection API
 */
export function useMobileDetection(): {
  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;
} {
  const context = useMobileContext();

  // Provide backwards compatible interface
  return {
    isMobile: context.isMobile,
    setIsMobile: () => {
      // Note: This is a no-op in the context version since state is managed automatically
      // The original hook allowed manual override, but context-based approach is automatic
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[useMobileDetection] setIsMobile is deprecated when using MobileContextProvider. ' +
            'Mobile state is now managed automatically based on viewport size.',
        );
      }
    },
  };
}

/**
 * Specialized hooks for common use cases
 */

/**
 * Hook that only returns device type - useful for components that only need this info
 */
export function useDeviceType() {
  const { deviceType } = useMobileContext();
  return deviceType;
}

/**
 * Hook that only returns mobile status - most common use case
 */
export function useIsMobile(): boolean {
  const { isMobile } = useMobileContext();
  return isMobile;
}

/**
 * Hook for viewport dimensions - useful for layout calculations
 */
export function useViewportSize() {
  const { viewportWidth, viewportHeight, orientation } = useMobileContext();
  return { width: viewportWidth, height: viewportHeight, orientation };
}

/**
 * Hook for breakpoint utilities - useful for responsive components
 */
export function useBreakpoints() {
  const { isExtraSmall, isSmall, isMedium, isLarge, isExtraLarge } = useMobileContext();
  return { isExtraSmall, isSmall, isMedium, isLarge, isExtraLarge };
}

/**
 * Hook for performance-aware features
 */
export function usePerformanceFeatures() {
  const { isLowMemoryDevice, preferReducedMotion } = useMobileContext();
  return { isLowMemoryDevice, preferReducedMotion };
}

/**
 * Hook for device-specific utilities
 */
export function useDeviceUtilities() {
  const { isPhone, isTablet, isDesktop } = useMobileContext();
  return { isPhone, isTablet, isDesktop };
}

/**
 * Custom hook for conditional rendering based on device type
 */
export function useDeviceConditional() {
  const context = useMobileContext();

  return {
    /**
     * Render content only on mobile devices
     */
    mobile: (content: React.ReactNode) => (context.isMobile ? content : null),

    /**
     * Render content only on tablet devices
     */
    tablet: (content: React.ReactNode) => (context.isTablet ? content : null),

    /**
     * Render content only on desktop devices
     */
    desktop: (content: React.ReactNode) => (context.isDesktop ? content : null),

    /**
     * Render different content based on device type
     */
    conditional: (options: { mobile?: React.ReactNode; tablet?: React.ReactNode; desktop?: React.ReactNode; fallback?: React.ReactNode }) => {
      if (context.isMobile && options.mobile) return options.mobile;
      if (context.isTablet && options.tablet) return options.tablet;
      if (context.isDesktop && options.desktop) return options.desktop;
      return options.fallback || null;
    },
  };
}

/**
 * Development utility hook for debugging mobile context
 */
export function useMobileContextDebug() {
  const context = useMobileContext({ debug: true });

  if (process.env.NODE_ENV !== 'development') {
    console.warn('[useMobileContextDebug] This hook should only be used in development');
  }

  return {
    context,
    /**
     * Log current context state to console
     */
    logContext: () => {
      console.table({
        'Device Type': context.deviceType,
        'Is Mobile': context.isMobile,
        Viewport: `${context.viewportWidth}x${context.viewportHeight}`,
        Orientation: context.orientation,
        'Low Memory': context.isLowMemoryDevice,
        'Reduced Motion': context.preferReducedMotion,
      });
    },
    /**
     * Get performance metrics
     */
    getMetrics: () => ({
      deviceType: context.deviceType,
      viewport: { width: context.viewportWidth, height: context.viewportHeight },
      features: {
        isLowMemoryDevice: context.isLowMemoryDevice,
        preferReducedMotion: context.preferReducedMotion,
      },
      breakpoints: {
        isExtraSmall: context.isExtraSmall,
        isSmall: context.isSmall,
        isMedium: context.isMedium,
        isLarge: context.isLarge,
        isExtraLarge: context.isExtraLarge,
      },
    }),
  };
}
